<template>
  <div>
    <QuickstartCard v-if="$route.query.quickstart">
      <template #header>👋 Post your first job</template>
      <template #body>
        We recommend posting at least 3 jobs and 7 cities to get started. Some of our favorite job titles are:
        <ul class="mt-4 list-disc pl-4">
          <li>Experienced Real Estate Agent</li>
          <li>Real Estate Agent - Inbound Leads Division</li>
          <li>Newly Licensed Real Estate Agent</li>
        </ul>
      </template>
    </QuickstartCard>

    <div v-if="!loadingSpinnerStore.loadingSpinner" class="flex flex-col gap-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="space-y-3">
          <p class="max-w-2xl text-sm text-slate-500">
            Keep active roles clean, spot stale postings quickly, and jump straight into the jobs that need attention.
          </p>

          <div class="flex flex-wrap gap-2">
            <Badge variant="gray">{{ totalActive }} active</Badge>
            <Badge variant="gray">{{ totalInactive }} inactive</Badge>
          </div>
        </div>

        <UiButton as-child>
          <router-link to="/jobs/new">New job</router-link>
        </UiButton>
      </div>

      <Card class="mb-0 gap-0 py-3">
        <div class="border-b border-gray-200 px-4 pb-3">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="space-y-1">
              <CardTitle class="text-base">Open roles</CardTitle>
              <p class="text-xs text-slate-500">{{ statusSummary }}</p>
            </div>

            <JobListFilters :filters="filters" @update-filter="handleNewFilter" />
          </div>
        </div>

        <div v-if="rows.length === 0" class="flex min-h-[260px] items-center justify-center px-6 py-6">
          <div class="flex max-w-sm flex-col items-center text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <Briefcase class="h-5 w-5" />
            </div>
            <p class="mt-4 text-sm font-medium text-slate-900">{{ emptyStateTitle }}</p>
            <p class="mt-1 text-sm text-slate-500">{{ emptyStateBody }}</p>
            <UiButton as-child size="sm" variant="outline" class="mt-4">
              <router-link to="/jobs/new">Create a job</router-link>
            </UiButton>
          </div>
        </div>

        <div v-else>
          <div
            class="hidden border-b border-gray-200 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:grid lg:grid-cols-[minmax(0,2fr)_140px_minmax(0,2.4fr)_180px_160px] lg:gap-4"
          >
            <span>Title</span>
            <span>Status</span>
            <span>Cities</span>
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('lastrefreshedat'))"
            >
              Last refreshed
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('lastrefreshedat')" />
            </button>
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('created'))"
            >
              Created
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('created')" />
            </button>
          </div>

          <div v-for="job in rows" :key="job.id" class="border-b border-gray-200 last:border-b-0">
            <div class="cursor-pointer px-4 py-3 transition-colors hover:bg-slate-50" @click="viewJob(job)">
              <div
                class="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,2fr)_140px_minmax(0,2.4fr)_180px_160px] lg:gap-4"
              >
                <div class="min-w-0 space-y-2">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="text-sm font-medium text-slate-900">{{ job.title }}</p>
                    <Badge v-if="job.isStale" variant="yellow" class="px-1.5 py-0 text-[11px] font-medium uppercase">
                      Stale
                    </Badge>
                  </div>
                  <p class="text-xs text-slate-500">{{ job.cities.length }} active cities</p>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">Status</p>
                  <Badge :variant="job.Status === 'Active' ? 'green' : 'gray'">
                    {{ job.Status }}
                  </Badge>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">Cities</p>
                  <div class="flex flex-wrap gap-1.5">
                    <Badge
                      v-for="city in visibleCities(job)"
                      :key="`${city.id}-${city.value}`"
                      variant="gray"
                      class="px-1.5 py-0 text-[11px] font-medium"
                    >
                      {{ city.value }}
                    </Badge>
                    <Popover v-if="job.cities.length > 4" :open="hoveredCityOverflowJobId === job.id">
                      <PopoverTrigger as-child>
                        <button
                          class="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-1.5 py-0 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-200"
                          @click.stop
                          @mouseenter="hoveredCityOverflowJobId = job.id"
                          @mouseleave="hoveredCityOverflowJobId = null"
                        >
                          +{{ job.cities.length - 4 }} more
                        </button>
                      </PopoverTrigger>

                      <PopoverContent
                        align="start"
                        class="w-64 p-3"
                        @click.stop
                        @mouseenter="hoveredCityOverflowJobId = job.id"
                        @mouseleave="hoveredCityOverflowJobId = null"
                      >
                        <div class="space-y-2">
                          <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
                            Additional cities
                          </p>
                          <div class="flex flex-wrap gap-1.5">
                            <Badge
                              v-for="city in hiddenCities(job)"
                              :key="`hidden-${city.id}-${city.value}`"
                              variant="gray"
                              class="px-1.5 py-0 text-[11px] font-medium"
                            >
                              {{ city.value }}
                            </Badge>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">
                    Last refreshed
                  </p>
                  <p class="text-sm text-slate-700">{{ formatStandardDateTime(job.lastRefreshedAt) }}</p>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">Created</p>
                  <div class="flex items-start justify-between gap-3">
                    <p class="text-sm text-slate-700">{{ formatStandardDateTime(job.created) }}</p>
                    <ChevronRight class="hidden h-4 w-4 shrink-0 text-slate-400 lg:block" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>

    <LoadingList />
  </div>
</template>

<script>
import { ArrowUpDown, Briefcase, ChevronRight } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button as UiButton } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { listJobs } from '../../api/jobs.ts';
import { formatStandardDateTime } from '../../utilities/format.ts';
import JobListFilters from './JobListFilters.vue';
import LoadingList from '../shared/LoadingList.vue';
import QuickstartCard from '../shared/QuickstartCard.vue';

export default {
  components: {
    ArrowUpDown,
    Badge,
    Briefcase,
    UiButton,
    Card,
    CardTitle,
    ChevronRight,
    JobListFilters,
    LoadingList,
    Popover,
    PopoverContent,
    PopoverTrigger,
    QuickstartCard,
  },
  setup() {
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { loadingSpinnerStore };
  },
  data() {
    return {
      hoveredCityOverflowJobId: null,
      jobs: [],
      totalActive: 0,
      totalInactive: 0,
    };
  },
  computed: {
    rows() {
      return this.jobs;
    },
    sort() {
      return {
        key: this.$route.query.sort || 'created',
        order: this.$route.query.order || 'desc',
      };
    },
    filters() {
      return [{ key: 'status', value: this.$route.query.status || 'All' }];
    },
    selectedStatusValue() {
      return this.$route.query.status || 'All';
    },
    statusSummary() {
      if (this.selectedStatusValue === 'All') {
        return `${this.totalActive} active and ${this.totalInactive} inactive jobs.`;
      }

      return `Showing ${this.selectedStatusValue.toLowerCase()} jobs.`;
    },
    emptyStateTitle() {
      if (this.selectedStatusValue === 'Active') return 'No active jobs';
      if (this.selectedStatusValue === 'Inactive') return 'No inactive jobs';
      return 'No jobs yet';
    },
    emptyStateBody() {
      if (this.selectedStatusValue === 'Active') {
        return 'There are no active postings right now. Create a new role or switch the filter to review inactive jobs.';
      }

      if (this.selectedStatusValue === 'Inactive') {
        return 'There are no inactive jobs to review right now.';
      }

      return 'Create your first job to start distributing roles across markets and tracking freshness.';
    },
  },
  mounted() {
    this.getJobs();
  },
  methods: {
    formatStandardDateTime,
    visibleCities(job) {
      return job.cities.slice(0, 4);
    },
    hiddenCities(job) {
      return job.cities.slice(4);
    },
    sortIconClass(key) {
      return this.sort.key === key ? 'text-slate-900' : 'text-slate-300';
    },
    nextSort(key) {
      if (this.sort.key === key) {
        return { key, order: this.sort.order === 'desc' ? 'asc' : 'desc' };
      }

      return { key, order: 'desc' };
    },
    async viewJob({ id }) {
      await this.$router.push({ name: 'JobDetail', params: { id } });
    },
    async getJobs() {
      this.loadingSpinnerStore.showLoadingSpinner();

      const queryParams = {
        status: this.$route.query.status,
        sort: this.$route.query.sort,
        order: this.$route.query.order,
      };

      const jobs = await listJobs(queryParams);

      this.jobs = jobs.records.map(job => ({
        title: job.title,
        Status: job.status,
        cities: job.job_locations
          .map(location => ({
            id: location.id,
            value: `${location.city}, ${location.state}`,
            status: location.status,
          }))
          .filter(location => location.status === 'active'),
        created: job.created_at,
        lastRefreshedAt: job.job_details.find(detail => detail?.status === 'active')?.created_at || job.created_at,
        id: job.id,
        isStale: job['is_stale?'],
      }));
      this.totalActive = jobs.total_active;
      this.totalInactive = jobs.total_inactive;

      this.loadingSpinnerStore.hideLoadingSpinner();
    },
    async handleNewSort({ key, order }) {
      await this.$router.push({
        path: '/jobs',
        query: {
          sort: key,
          order,
          status: this.$route.query.status || 'All',
        },
      });

      await this.getJobs();
    },
    async handleNewFilter(newFilters) {
      await this.$router.push({
        path: '/jobs',
        query: {
          sort: this.sort.key,
          order: this.sort.order,
          status: newFilters.status.value || 'All',
        },
      });

      await this.getJobs();
    },
  },
};
</script>
